import { useEffect, useMemo, useState } from "react";

import { useUpdateTenantStyles } from "../../../hooks/mutations/admin/hospitals";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  DEFAULT_TENANT_STYLES,
  isDefaultTenantStyles,
  TENANT_STYLE_GROUPS,
} from "../../../utils/tenantStyles";
import {
  FieldHint,
  FilterButton,
  SectionActions,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
} from "../shared";
import { STYLE_GROUP_PREVIEWS } from "./TenantStylePreviews";
import {
  StyleGroup,
  StyleGroupBody,
  StyleGroupCount,
  StyleGroupPreview,
  StyleHexInput,
  StyleSwatch,
  StyleTokenControls,
  StyleTokenGrid,
  StyleTokenName,
  StyleTokenRow,
} from "./TenantStyles.styles";
import { useTenantThemeClass } from "./useTenantThemeClass";

interface ITenantStylesEditorProps {
  tenantId: string;
  /** Already merged over the defaults by `useTenantStyles`. */
  styles: Record<string, string>;
}

/** `#rgb`, `#rrggbb` or `#rrggbbaa` — the forms the saved themes use. */
const HEX_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** <input type="color"> only understands #rrggbb, so alpha is trimmed for it. */
function toSwatchValue(value: string): string {
  if (/^#[0-9a-fA-F]{8}$/.test(value)) return value.slice(0, 7);
  if (/^#[0-9a-fA-F]{3}$/.test(value)) {
    // charAt, not destructuring: iterating a string needs downlevelIteration
    // under the ES5 target, and `tsc --noEmit` doesn't catch that — only the
    // real build does.
    const r = value.charAt(1);
    const g = value.charAt(2);
    const b = value.charAt(3);
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return HEX_PATTERN.test(value) ? value : "#000000";
}

/**
 * Tenant theme editor (EN-5154) — Retool's grouped colour pickers, each
 * alongside a live mock of the portal area it restyles.
 *
 * The whole token object is saved every time, which is what Retool did and
 * what the portal's `applyColorsPatch` expects to read back: a partial object
 * would leave the omitted variables unset at login rather than defaulted.
 */
export function TenantStylesEditor({
  tenantId,
  styles,
}: ITenantStylesEditorProps) {
  const { showToast } = useToast();
  const { updateTenantStyles, isUpdatingStyles } = useUpdateTenantStyles();

  const [draft, setDraft] = useState<Record<string, string>>(styles);

  // The previews render real portal components; this is the tenant's palette
  // as a scoped Stitches theme — the same patch applyColorsPatch puts on
  // <html> at login.
  const previewThemeClass = useTenantThemeClass(draft);

  // Re-seed when a different tenant's theme arrives.
  useEffect(() => {
    setDraft(styles);
  }, [styles]);

  const invalidTokens = useMemo(
    () =>
      Object.entries(draft)
        .filter(([, value]) => !HEX_PATTERN.test(value))
        .map(([token]) => token),
    [draft]
  );

  const isDirty = useMemo(
    () =>
      Object.keys(DEFAULT_TENANT_STYLES).some((t) => draft[t] !== styles[t]),
    [draft, styles]
  );

  const setToken = (token: string, value: string) =>
    setDraft((prev) => ({ ...prev, [token]: value }));

  const handleSave = async () => {
    if (invalidTokens.length > 0) return;

    try {
      await updateTenantStyles({ tenantId, styles: draft });
      showToast("Successfully updated tenant styles", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating the tenant styles."
        ),
        "warning"
      );
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>Theme</SectionTitle>
          <StyleGroupCount>
            {Object.keys(DEFAULT_TENANT_STYLES).length} colours
          </StyleGroupCount>
        </SectionHeading>
        <FilterButton
          variant="secondary"
          disabled={isUpdatingStyles || isDefaultTenantStyles(draft)}
          onClick={() => setDraft({ ...DEFAULT_TENANT_STYLES })}
        >
          Reset to defaults
        </FilterButton>
      </SectionHeader>

      <FieldHint>
        These colours restyle the tenant&apos;s portal for everyone in it. The
        previews show the affected areas with the current values.
      </FieldHint>

      {TENANT_STYLE_GROUPS.map((group) => {
        const Preview = STYLE_GROUP_PREVIEWS[group.id];

        return (
          <StyleGroup key={group.id}>
            <summary>
              {group.label}
              <StyleGroupCount>{group.tokens.length} colours</StyleGroupCount>
            </summary>

            {/* The previews read `var(--<token>)`, so the draft is applied
                here as custom properties rather than threaded as props. */}
            <StyleGroupBody>
              <StyleTokenGrid>
                {group.tokens.map((token) => {
                  const value = draft[token] ?? "";
                  const isInvalid = !HEX_PATTERN.test(value);

                  return (
                    <StyleTokenRow key={token} title={token}>
                      <StyleTokenName>{token}</StyleTokenName>
                      <StyleTokenControls>
                        <StyleSwatch
                          type="color"
                          aria-label={`${token} colour`}
                          value={toSwatchValue(value)}
                          onChange={(e) => setToken(token, e.target.value)}
                        />
                        <StyleHexInput
                          aria-label={`${token} hex`}
                          invalid={isInvalid}
                          value={value}
                          onChange={(e) => setToken(token, e.target.value)}
                        />
                      </StyleTokenControls>
                    </StyleTokenRow>
                  );
                })}
              </StyleTokenGrid>

              {/* Scoping the theme class here is what makes the real
                  components below render as this tenant's portal does. */}
              {Preview && (
                <StyleGroupPreview className={previewThemeClass}>
                  <Preview />
                </StyleGroupPreview>
              )}
            </StyleGroupBody>
          </StyleGroup>
        );
      })}

      <SectionActions>
        {invalidTokens.length > 0 && (
          <FieldHint>
            {invalidTokens.length} colour
            {invalidTokens.length === 1 ? "" : "s"} need a hex value like
            #7562A8.
          </FieldHint>
        )}
        <FilterButton
          variant="primary"
          disabled={isUpdatingStyles || invalidTokens.length > 0 || !isDirty}
          onClick={handleSave}
        >
          {isUpdatingStyles ? "Saving..." : "Save theme"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
